import type { DepGraph, DepGraphPredType } from "../../../../models";

export interface ActivityBox {
  rowIndex: number;
  /** The drawn bar's full envelope (progress + gap + remaining). */
  left: number;
  right: number;
  top: number;
  bottom: number;
  midY: number;
  /** Where dependency arrows ATTACH — the REMAINING-work (driving) edges, which
   *  differ from the bar envelope for a progressed activity. FS/SS land on
   *  driveStartX (the remaining start); FF/SF on driveFinishX. */
  driveStartX: number;
  driveFinishX: number;
  /** Placement zone of this box (takt view), or null in by-date view. Used to
   *  pair a duplicated neighbour copy with the bar in the same zone. */
  zoneKey: string | null;
  /** Anchors view: this box is an in-section context copy laid adjacent to the
   *  named external. It only carries edges between itself and that external —
   *  per `anchorRole` — so each tie draws one short hop and section-to-section
   *  edges never stretch across the anchor groups. */
  anchorExternalId?: string;
  /** "fed" = the external is this copy's predecessor; "feeder" = this copy is
   *  the external's predecessor. */
  anchorRole?: "feeder" | "fed";
}

export interface ArrowPath {
  /** Stable id: committed edge id (or `${predId}->${succId}` for a draft edge),
   *  suffixed with the connected rows so duplicated copies stay distinct. */
  arrowId: string;
  predId: string;
  succId: string;
  type: DepGraphPredType;
  d: string;
  /** Endpoints' in_scope differ — drawn dashed/dimmed. */
  crosses: boolean;
  /** This pred is the binding/driving dependency for its successor (forward
   *  pass) — drawn solid/emphasized; slack preds are faint. */
  driving: boolean;
}

const STUB = 6; // min horizontal extension off pred for FS/SF before turning

/**
 * Build orthogonal step paths for every edge in the flat graph, resolving each
 * endpoint to its rendered box(es). An activity can be laid out in more than one
 * row (a neighbour duplicated across zones), so each endpoint maps to a LIST of
 * boxes; the edge connects the copies that share a placement zone (falling back
 * to the vertically nearest pair). Edges with an unlaid endpoint are skipped.
 *
 * MS-Project-style two-segment L: horizontal from pred's anchor along pred's
 * mid-Y to the landing X, then vertical onto succ's top/bottom edge.
 */
export function computeArrows(
  graph: DepGraph,
  boxesByActivity: Map<string, ActivityBox[]>,
): ArrowPath[] {
  const out: ArrowPath[] = [];
  for (const [succId, edges] of Object.entries(graph.edges)) {
    const allSuccBoxes = boxesByActivity.get(succId);
    if (!allSuccBoxes || allSuccBoxes.length === 0) continue;
    for (const e of edges) {
      const allPredBoxes = boxesByActivity.get(e.pred_id);
      if (!allPredBoxes || allPredBoxes.length === 0) continue;
      // An anchor-partner copy hosts only its own pairing's edges: as pred it
      // must be the FEEDER copy of this edge's successor; as succ the FED copy
      // of this edge's predecessor. Plain boxes host every edge as before.
      const predBoxes = allPredBoxes.filter(
        (b) => !b.anchorExternalId || (b.anchorExternalId === succId && b.anchorRole === "feeder"),
      );
      const succBoxes = allSuccBoxes.filter(
        (b) => !b.anchorExternalId || (b.anchorExternalId === e.pred_id && b.anchorRole === "fed"),
      );
      if (predBoxes.length === 0 || succBoxes.length === 0) continue;
      const base =
        e.edge_id != null
          ? `e-${e.edge_id}`
          : `${e.pred_id}->${succId}-${e.type}`;
      for (const [pred, succ] of resolvePairs(predBoxes, succBoxes)) {
        out.push({
          arrowId: `${base}#${pred.rowIndex}-${succ.rowIndex}`,
          predId: e.pred_id,
          succId,
          type: e.type,
          d: pathFor(e.type, pred, succ),
          crosses: e.crosses,
          driving: e.driving,
        });
      }
    }
  }
  return out;
}

/** Which (pred, succ) box copies an edge connects. Shared-zone copies pair up
 *  (a neighbour copy to the bar in its zone); with no shared zone, the single
 *  vertically-nearest pair is drawn. */
function resolvePairs(
  predBoxes: ActivityBox[],
  succBoxes: ActivityBox[],
): Array<[ActivityBox, ActivityBox]> {
  if (predBoxes.length === 1 && succBoxes.length === 1) {
    return [[predBoxes[0], succBoxes[0]]];
  }
  const succByZone = new Map<string, ActivityBox>();
  for (const b of succBoxes) if (b.zoneKey) succByZone.set(b.zoneKey, b);
  const pairs: Array<[ActivityBox, ActivityBox]> = [];
  for (const p of predBoxes) {
    if (p.zoneKey) {
      const s = succByZone.get(p.zoneKey);
      if (s) pairs.push([p, s]);
    }
  }
  if (pairs.length > 0) return pairs;

  let best: [ActivityBox, ActivityBox] | null = null;
  let bestDist = Infinity;
  for (const p of predBoxes) {
    for (const s of succBoxes) {
      const dist = Math.abs(p.midY - s.midY);
      if (dist < bestDist) {
        bestDist = dist;
        best = [p, s];
      }
    }
  }
  return best ? [best] : [];
}

function pathFor(type: DepGraphPredType, pred: ActivityBox, succ: ActivityBox): string {
  const predRightSide = type === "FS" || type === "FF";
  const succRightSide = type === "FF" || type === "SF";
  // FS/FF leave from the pred's remaining FINISH; SS/SF from its remaining START.
  // The arrowhead lands ON the successor's remaining start (FS/SS) or finish
  // (FF/SF) — the REMAINING-work edges, never the actual-start, so an
  // out-of-sequence in-progress activity's arrows still read forward.
  const sx = predRightSide ? pred.driveFinishX : pred.driveStartX;
  const targetX = succRightSide ? succ.driveFinishX : succ.driveStartX;
  const sy = pred.midY;

  const succBelow = succ.midY > pred.midY;
  const landY = succBelow ? succ.top : succ.bottom;

  // FS/SF must clear the predecessor by a STUB before turning. A NEGATIVE lag
  // (lead) puts the successor's landing edge "behind" that exit point — the
  // successor starts before the predecessor finishes (FS) — so we can't just
  // drop straight down or we'd land in the middle of the bar. Route around:
  // out by the stub, down to mid-gap, back across to the landing edge, then onto
  // it — keeping the arrowhead on the successor's start/finish edge.
  const exitX = predRightSide ? sx + STUB : sx - STUB;
  const needsRoute =
    (type === "FS" && targetX < exitX) || (type === "SF" && targetX > exitX);
  if (needsRoute) {
    const midY = (sy + landY) / 2;
    return [
      `M ${sx} ${sy}`,
      `L ${exitX} ${sy}`,
      `L ${exitX} ${midY}`,
      `L ${targetX} ${midY}`,
      `L ${targetX} ${landY}`,
    ].join(" ");
  }

  if (Math.abs(sy - landY) < 1) {
    return `M ${sx} ${sy} L ${targetX} ${succ.midY}`;
  }

  return [
    `M ${sx} ${sy}`,
    `L ${targetX} ${sy}`,
    `L ${targetX} ${landY}`,
  ].join(" ");
}
